import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent } from './ui/card';
import { Plus, Trash2, Calendar, Clock, Users, Wrench, ChevronDown, ChevronUp, PenTool, Check, Mail, FileText, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import PhotoUpload, { uploadPendingPhotos } from './PhotoUpload';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Activity types - alphabetically sorted
const DEFAULT_ACTIVITY_TYPES = [
  'Altro', 'Derattizzazione', 'Disinfestazione', 'Pulizia ordinaria', 
  'Pulizia straordinaria', 'Pulizia vetri', 'Sanificazione', 'Trattamento pavimenti'
];

// Signature Pad Component (copied from ReportsPage for consistency)
const SignaturePad = ({ onSave, onClear, savedSignature, label, required = true }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);

  useEffect(() => {
    if (isZoomed) {
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      
      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';
        window.scrollTo(0, scrollY);
      };
    }
  }, [isZoomed]);

  useEffect(() => {
    const initCanvas = () => {
      if (canvasRef.current && !savedSignature) {
        const ctx = canvasRef.current.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
        ctx.strokeStyle = '#1B6B93';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
      }
    };
    
    if (isZoomed) {
      setTimeout(initCanvas, 50);
    } else {
      initCanvas();
    }
  }, [savedSignature, isZoomed]);

  const getCoords = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const coords = getCoords(e); ctx.beginPath(); ctx.moveTo(coords.x, coords.y); setIsDrawing(true); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const coords = getCoords(e); ctx.lineTo(coords.x, coords.y); ctx.stroke(); };
  const stopDrawing = () => setIsDrawing(false);
  const clearCanvas = () => { const ctx = canvasRef.current.getContext('2d'); ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height); };
  const saveSignature = () => { onSave(canvasRef.current.toDataURL('image/png')); setIsZoomed(false); toast.success('Firma salvata!'); };
  const cancelZoom = () => { setIsZoomed(false); };

  if (savedSignature) {
    return (
      <div className="space-y-2">
        <Label className="flex items-center gap-2 text-emerald-600"><Check className="w-4 h-4" /> {label} - Firmato</Label>
        <div className="border rounded-lg p-2 bg-emerald-50"><img src={savedSignature} alt="Firma" className="max-h-20 mx-auto" /></div>
        <Button type="button" variant="outline" size="sm" onClick={onClear}>Cancella e Rifirma</Button>
      </div>
    );
  }

  const zoomedOverlay = isZoomed ? ReactDOM.createPortal(
    <>
      <div 
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancelZoom(); }}
        onTouchEnd={(e) => { e.preventDefault(); e.stopPropagation(); cancelZoom(); }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.92)', zIndex: 999998 }}
      />
      <div 
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        className="bg-white rounded-xl p-4 shadow-2xl"
        style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 'calc(100% - 2rem)', maxWidth: '28rem', zIndex: 999999, pointerEvents: 'auto' }}
      >
        <Label className="flex items-center gap-2 mb-3 text-lg font-semibold"><PenTool className="w-5 h-5 text-[#1B6B93]" /> {label}</Label>
        <div className="border-2 border-[#1B6B93] rounded-lg p-2 bg-white">
          <canvas 
            ref={canvasRef} 
            width={500} 
            height={200} 
            className="w-full cursor-crosshair" 
            style={{height: '200px', touchAction: 'none'}}
            onMouseDown={startDrawing} 
            onMouseMove={draw} 
            onMouseUp={stopDrawing} 
            onMouseLeave={stopDrawing} 
            onTouchStart={startDrawing} 
            onTouchMove={draw} 
            onTouchEnd={stopDrawing} 
          />
        </div>
        <p className="text-xs text-slate-500 mt-2 text-center">Firma con il dito o il mouse nell'area sopra</p>
        <div className="flex gap-2 mt-4">
          <Button type="button" variant="outline" className="flex-1" onClick={clearCanvas}>Cancella</Button>
          <Button type="button" variant="outline" className="flex-1" onClick={cancelZoom}>Annulla</Button>
          <Button type="button" className="flex-1 bg-[#1B6B93] hover:bg-[#155a7a] text-white" onClick={saveSignature}><Check className="w-4 h-4 mr-1" /> Conferma</Button>
        </div>
      </div>
    </>,
    document.body
  ) : null;

  return (
    <>
      {zoomedOverlay}
      <div className="space-y-2">
        <Label className="flex items-center gap-2"><PenTool className="w-4 h-4" /> {label} {required && '*'}</Label>
        {/* Mobile: Click to open zoomed view */}
        <div 
          className="md:hidden border-2 border-dashed border-slate-300 rounded-lg p-4 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
          onClick={() => setIsZoomed(true)}
        >
          <div className="flex flex-col items-center justify-center text-slate-500">
            <PenTool className="w-8 h-8 mb-2" />
            <span className="text-sm font-medium">Tocca per firmare</span>
            <span className="text-xs">(si aprirà un'area più grande)</span>
          </div>
        </div>
        {/* Desktop: Direct canvas */}
        <div className="hidden md:block border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
          <canvas ref={canvasRef} width={400} height={120} className="w-full cursor-crosshair touch-none" onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing} onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} />
        </div>
        <div className="hidden md:flex gap-2">
          <Button type="button" variant="outline" size="sm" onClick={clearCanvas}>Cancella</Button>
          <Button type="button" size="sm" onClick={saveSignature} className="bg-[#4BA3C7] hover:bg-[#3a8aab] text-white"><Check className="w-4 h-4 mr-1" /> Conferma</Button>
        </div>
      </div>
    </>
  );
};

const MultiDayReportForm = ({ sites, employees, editingReport, duplicateData, onSuccess, onCancel, pendingTickets = [], activityTypes: propActivityTypes }) => {
  const { isAdminOrGestore, user } = useAuth();
  const ACTIVITY_TYPES = propActivityTypes || DEFAULT_ACTIVITY_TYPES;
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [formData, setFormData] = useState({
    site_id: '',
    activity_type: '',
    ticket: '',
    ticket_id: '', // ID del ticket dall'app Ticket
    notes: '',
    client_email: '',
    workdays: [createEmptyWorkday()],
    // Signature fields - precompila con l'utente loggato
    employee_signer_name: '',
    client_signer_name: '',
    employee_signature: '',
    client_signature: '',
    // Approval checkbox
    send_approval_email: false,
    // PDF senza ore per cliente
    client_report_no_hours: false,
    // Skip email (solo admin)
    skip_email_send: false
  });
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const [expandedDays, setExpandedDays] = useState([0]);
  const [equipmentSuggestions, setEquipmentSuggestions] = useState([]);
  // Stato per aggiunta nuovo cantiere
  const [showNewSiteForm, setShowNewSiteForm] = useState(false);
  const [newSiteData, setNewSiteData] = useState({ name: '', address: '', client_name: '', client_email: '' });
  const [creatingSite, setCreatingSite] = useState(false);
  const [localSites, setLocalSites] = useState(sites);
  // Stato per i valori di input delle ore attrezzature (stringhe per permettere virgola/punto)
  const [equipmentHoursInput, setEquipmentHoursInput] = useState({});
  // Stato per foto in attesa di upload (nuovi rapporti)
  const [pendingPhotos, setPendingPhotos] = useState([]);

  // Carica i dati del rapporto in modifica O duplicazione
  useEffect(() => {
    const sourceData = editingReport || duplicateData;
    if (sourceData) {
      setFormData({
        site_id: sourceData.site_id || '',
        activity_type: sourceData.activity_type || '',
        ticket: sourceData.ticket || '',
        notes: sourceData.notes || '',
        client_email: sourceData.client_email || '',
        workdays: sourceData.workdays?.length > 0 ? sourceData.workdays : [createEmptyWorkday()],
        // Per duplicazione, non copiare le firme (nuovo rapporto)
        employee_signer_name: editingReport ? (sourceData.employee_signer_name || '') : '',
        client_signer_name: editingReport ? (sourceData.client_signer_name || '') : '',
        employee_signature: editingReport ? (sourceData.employee_signature || '') : '',
        client_signature: editingReport ? (sourceData.client_signature || '') : '',
        send_approval_email: false,
        client_report_no_hours: sourceData.client_report_no_hours || false,
        skip_email_send: sourceData.skip_email_send || false
      });
      // Espandi tutte le giornate
      if (sourceData.workdays?.length > 0) {
        setExpandedDays(sourceData.workdays.map((_, i) => i));
      }
    }
  }, [editingReport, duplicateData]);

  // Precompila il firmatario dipendente con l'utente loggato (solo per nuovi rapporti, non in modifica)
  useEffect(() => {
    if (user?.name && !formData.employee_signer_name && !editingReport && !duplicateData) {
      setFormData(prev => ({ ...prev, employee_signer_name: user.name }));
    }
    // Per duplicazione, precompila comunque il firmatario
    if (user?.name && duplicateData && !formData.employee_signer_name) {
      setFormData(prev => ({ ...prev, employee_signer_name: user.name }));
    }
  }, [user, editingReport, duplicateData, formData.employee_signer_name]);

  useEffect(() => {
    setLocalSites(sites);
  }, [sites]);

  useEffect(() => {
    fetchEquipmentSuggestions();
  }, []);

  const fetchEquipmentSuggestions = async () => {
    try {
      const response = await axios.get(`${API}/equipment`);
      // Sort equipment alphabetically
      const sorted = (response.data.equipment || []).sort((a, b) => a.localeCompare(b, 'it'));
      setEquipmentSuggestions(sorted);
    } catch (error) {
      console.error('Failed to fetch equipment suggestions');
    }
  };

  const handleCreateSite = async () => {
    if (!newSiteData.name.trim()) {
      toast.error('Nome cantiere obbligatorio');
      return;
    }
    setCreatingSite(true);
    try {
      const response = await axios.post(`${API}/sites`, newSiteData);
      const newSite = response.data;
      setLocalSites([...localSites, newSite].sort((a, b) => a.name.localeCompare(b.name, 'it')));
      setFormData({ ...formData, site_id: newSite.id, client_email: newSite.client_email || '' });
      setShowNewSiteForm(false);
      setNewSiteData({ name: '', address: '', client_name: '', client_email: '' });
      toast.success('Cantiere creato!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreatingSite(false);
    }
  };

  function createEmptyWorkday() {
    return {
      date: new Date().toISOString().split('T')[0],
      start_time: '08:00',
      end_time: '17:00',
      activity_type: '',
      employees: [],
      equipment: [],
      notes: ''
    };
  }

  const handleSiteChange = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    setFormData({
      ...formData,
      site_id: siteId,
      client_email: site?.client_email || formData.client_email
    });
  };

  const addWorkday = () => {
    const lastDay = formData.workdays[formData.workdays.length - 1];
    const newDay = {
      ...createEmptyWorkday(),
      date: lastDay?.date || new Date().toISOString().split('T')[0],
      start_time: lastDay?.start_time || '08:00',
      end_time: lastDay?.end_time || '17:00'
    };
    // Increment date by 1 day
    const nextDate = new Date(newDay.date);
    nextDate.setDate(nextDate.getDate() + 1);
    newDay.date = nextDate.toISOString().split('T')[0];
    
    setFormData({
      ...formData,
      workdays: [...formData.workdays, newDay]
    });
    setExpandedDays([...expandedDays, formData.workdays.length]);
  };

  const removeWorkday = (index) => {
    if (formData.workdays.length <= 1) {
      toast.error('Deve esserci almeno una giornata');
      return;
    }
    const newWorkdays = formData.workdays.filter((_, i) => i !== index);
    setFormData({ ...formData, workdays: newWorkdays });
    setExpandedDays(expandedDays.filter(i => i !== index).map(i => i > index ? i - 1 : i));
  };

  const updateWorkday = (index, field, value) => {
    const newWorkdays = [...formData.workdays];
    newWorkdays[index] = { ...newWorkdays[index], [field]: value };
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const toggleDayExpanded = (index) => {
    if (expandedDays.includes(index)) {
      setExpandedDays(expandedDays.filter(i => i !== index));
    } else {
      setExpandedDays([...expandedDays, index]);
    }
  };

  const addEmployeeToDay = (dayIndex, employee) => {
    const newWorkdays = [...formData.workdays];
    const day = newWorkdays[dayIndex];
    
    if (day.employees.find(e => e.employee_id === employee.id)) {
      toast.error('Dipendente già aggiunto a questa giornata');
      return;
    }
    
    day.employees.push({
      employee_id: employee.id,
      employee_name: employee.name,
      employee_matricola: employee.matricola || '',
      start_time: day.start_time,
      end_time: day.end_time,
      hours: calculateHours(day.start_time, day.end_time)
    });
    
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const removeEmployeeFromDay = (dayIndex, empIndex) => {
    const newWorkdays = [...formData.workdays];
    newWorkdays[dayIndex].employees.splice(empIndex, 1);
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const updateEmployeeInDay = (dayIndex, empIndex, field, value) => {
    const newWorkdays = [...formData.workdays];
    newWorkdays[dayIndex].employees[empIndex][field] = value;
    
    // Recalculate hours if time changed
    if (field === 'start_time' || field === 'end_time') {
      const emp = newWorkdays[dayIndex].employees[empIndex];
      emp.hours = calculateHours(emp.start_time, emp.end_time);
    }
    
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const addEquipmentToDay = (dayIndex) => {
    const newWorkdays = [...formData.workdays];
    const day = newWorkdays[dayIndex];
    // Inizializza con gli orari della giornata
    newWorkdays[dayIndex].equipment.push({ 
      equipment_name: '', 
      start_time: day.start_time || '', 
      end_time: day.end_time || '' 
    });
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const removeEquipmentFromDay = (dayIndex, eqIndex) => {
    const newWorkdays = [...formData.workdays];
    newWorkdays[dayIndex].equipment.splice(eqIndex, 1);
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const updateEquipmentInDay = (dayIndex, eqIndex, field, value) => {
    const newWorkdays = [...formData.workdays];
    newWorkdays[dayIndex].equipment[eqIndex][field] = value;
    setFormData({ ...formData, workdays: newWorkdays });
  };

  const calculateHours = (startTime, endTime) => {
    const [startH, startM] = startTime.split(':').map(Number);
    const [endH, endM] = endTime.split(':').map(Number);
    let hours = endH - startH + (endM - startM) / 60;
    if (hours < 0) hours += 24; // Handle overnight shifts
    return Math.round(hours * 100) / 100;
  };

  const calculateTotals = () => {
    let totalHours = 0;
    let totalEmployeeEntries = 0;
    const uniqueEmployees = new Set();
    
    formData.workdays.forEach(day => {
      day.employees.forEach(emp => {
        totalHours += emp.hours || 0;
        totalEmployeeEntries++;
        uniqueEmployees.add(emp.employee_id);
      });
    });
    
    return {
      totalHours: Math.round(totalHours * 100) / 100,
      numWorkdays: formData.workdays.length,
      numEmployees: uniqueEmployees.size,
      totalEmployeeEntries
    };
  };

  const handleSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    
    // Guardia anti doppio click
    if (submittingRef.current) return;
    
    // Per le bozze, validazione meno stringente
    if (!saveAsDraft) {
      if (!formData.site_id) {
        toast.error('Seleziona un cantiere');
        return;
      }
      if (!formData.activity_type) {
        toast.error('Seleziona il tipo di attività');
        return;
      }
      
      // Validate workdays
      for (let i = 0; i < formData.workdays.length; i++) {
        const day = formData.workdays[i];
        if (day.employees.length === 0) {
          toast.error(`Giornata ${i + 1}: aggiungi almeno un dipendente`);
          return;
        }
      }
      
      // Validate signatures for employees (required)
      // For admin/gestore, signatures are optional
      if (!isAdminOrGestore()) {
        if (!formData.employee_signer_name) {
          toast.error('Nome dipendente firmatario obbligatorio');
          return;
        }
        if (!formData.employee_signature) {
          toast.error('Firma dipendente obbligatoria');
          return;
        }
        if (!formData.client_signer_name) {
          toast.error('Nome cliente firmatario obbligatorio');
          return;
        }
        if (!formData.client_signature) {
          toast.error('Firma cliente obbligatoria');
          return;
        }
      }
      
      // If sending approval email, client email is required
      if (formData.send_approval_email && !formData.client_email) {
        toast.error('Email cliente obbligatoria per inviare richiesta approvazione');
        return;
      }
      
      // Se entrambe le firme presenti (invio automatico email finale), serve l'email cliente
      if (formData.employee_signature && formData.client_signature && !formData.client_email && !formData.skip_email_send) {
        toast.error('Email cliente obbligatoria per inviare il rapporto firmato');
        return;
      }
    } else {
      // Per bozze: richiedi almeno un cantiere
      if (!formData.site_id) {
        toast.error('Seleziona un cantiere per salvare la bozza');
        return;
      }
    }
    
    setSubmitting(true);
    submittingRef.current = true;
    try {
      const payload = { ...formData };
      // Normalizza orari attrezzature: eredita orari giornata se mancanti
      payload.workdays = (formData.workdays || []).map(day => ({
        ...day,
        equipment: (day.equipment || []).map(eq => ({
          ...eq,
          start_time: eq.start_time || day.start_time || '',
          end_time: eq.end_time || day.end_time || ''
        }))
      }));
      if (saveAsDraft) {
        payload.is_draft = true;
      }
      
      if (editingReport) {
        // Aggiornamento rapporto esistente
        await axios.put(`${API}/reports/${editingReport.id}`, payload);
        toast.success('Rapporto multi-giorno aggiornato!');
      } else {
        // Se ci sono foto pendenti, ritarda l'invio dell'email
        if (pendingPhotos.length > 0) {
          payload.delay_email_for_photos = true;
        }
        
        // Creazione nuovo rapporto
        const response = await axios.post(`${API}/reports/multi-day`, payload);
        const createdReport = response.data;
        
        // Upload delle foto pendenti dopo la creazione del rapporto
        if (pendingPhotos.length > 0 && createdReport?.id) {
          toast.info(`Caricamento ${pendingPhotos.length} foto in corso...`);
          const uploaded = await uploadPendingPhotos(createdReport.id, pendingPhotos);
          if (uploaded.length > 0) {
            toast.success(`${uploaded.length} foto caricate con successo!`);
          }
          
          // Ora triggera l'invio dell'email con le foto allegate
          if (formData.employee_signature && formData.client_signature && formData.client_email && !formData.skip_email_send && !saveAsDraft) {
            try {
              await axios.post(`${API}/reports/${createdReport.id}/send-email-after-photos`);
              toast.success('Email con foto in invio al cliente!');
            } catch (emailError) {
              console.error('Error triggering email:', emailError);
            }
          }
        }
        
        if (saveAsDraft) {
          toast.success('Bozza salvata! Potrai completarla in seguito.');
        } else if (formData.send_approval_email && !formData.client_signature) {
          toast.success('Rapporto creato e richiesta approvazione inviata!');
        } else {
          toast.success('Rapporto multi-giorno creato!');
        }
      }
      onSuccess();
    } catch (error) {
      if (error.response?.status === 409) {
        toast.warning('Rapporto già inviato. Attendi qualche secondo prima di riprovare.');
      } else {
        toast.error(error.response?.data?.detail || 'Errore nella creazione');
      }
    } finally {
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  const totals = calculateTotals();
  const showApprovalCheckbox = isAdminOrGestore() && !formData.client_signature;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Header Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Cantiere *</Label>
          <div className="flex gap-2">
            <Select value={formData.site_id} onValueChange={handleSiteChange}>
              <SelectTrigger className="bg-white flex-1">
                <SelectValue placeholder="Seleziona cantiere" />
              </SelectTrigger>
              <SelectContent>
                {localSites.map(site => (
                  <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              type="button" 
              variant="outline" 
              size="icon"
              onClick={() => setShowNewSiteForm(!showNewSiteForm)}
              className="shrink-0"
              title="Aggiungi nuovo cantiere"
            >
              <Building2 className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Mini-form per nuovo cantiere */}
          {showNewSiteForm && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
              <p className="text-sm font-medium text-blue-800">Nuovo Cantiere/Cliente</p>
              <Input
                value={newSiteData.name}
                onChange={(e) => setNewSiteData({ ...newSiteData, name: e.target.value })}
                placeholder="Nome cantiere *"
                className="bg-white h-9"
              />
              <Input
                value={newSiteData.address}
                onChange={(e) => setNewSiteData({ ...newSiteData, address: e.target.value })}
                placeholder="Indirizzo"
                className="bg-white h-9"
              />
              <Input
                value={newSiteData.client_name}
                onChange={(e) => setNewSiteData({ ...newSiteData, client_name: e.target.value })}
                placeholder="Nome cliente"
                className="bg-white h-9"
              />
              <Input
                type="email"
                value={newSiteData.client_email}
                onChange={(e) => setNewSiteData({ ...newSiteData, client_email: e.target.value })}
                placeholder="Email cliente"
                className="bg-white h-9"
              />
              <div className="flex gap-2">
                <Button type="button" size="sm" onClick={handleCreateSite} disabled={creatingSite} className="bg-blue-600 hover:bg-blue-700">
                  {creatingSite ? 'Creazione...' : 'Crea Cantiere'}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => setShowNewSiteForm(false)}>
                  Annulla
                </Button>
              </div>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <Label>Email Cliente {formData.send_approval_email && '*'}</Label>
          <Input
            type="email"
            value={formData.client_email}
            onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
            placeholder="cliente@email.com"
            className="bg-white"
          />
        </div>
      </div>

      {/* Activity Type - Dropdown */}
      <div className="space-y-2">
        <Label>Tipo di Attività *</Label>
        <Select value={formData.activity_type} onValueChange={(v) => setFormData({ ...formData, activity_type: v })}>
          <SelectTrigger className="bg-white">
            <SelectValue placeholder="Seleziona tipo di attività" />
          </SelectTrigger>
          <SelectContent>
            {ACTIVITY_TYPES.map(a => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Ns./Vs. Rif. (opzionale)</Label>
          <Input
            value={formData.ticket}
            onChange={(e) => setFormData({ ...formData, ticket: e.target.value })}
            placeholder="Riferimento"
            className="bg-white"
          />
        </div>
        <div className="space-y-2">
          <Label>Note Generali</Label>
          <Input
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Note"
            className="bg-white"
          />
        </div>
      </div>

      {/* Ticket da App Ticket */}
      {!editingReport && (
        pendingTickets.length > 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-blue-700 font-medium">
              <FileText className="w-5 h-5" />
              <span>Ticket Clienti ({pendingTickets.length} in attesa)</span>
            </div>
            <p className="text-sm text-blue-600">
              Seleziona un ticket per collegare questo rapporto all'intervento richiesto.
            </p>
            <Select
              value={selectedTicket?.ticket_id || "none"}
              onValueChange={(value) => {
                if (value === "none") {
                  setSelectedTicket(null);
                  return;
                }
                const ticket = pendingTickets.find(t => t.ticket_id === value);
                if (ticket) {
                  setSelectedTicket(ticket);
                  // Pre-popola i campi dal ticket
                  const site = localSites.find(s => 
                    s.name.toLowerCase().includes(ticket.cantiere_name?.toLowerCase()) ||
                    ticket.cantiere_name?.toLowerCase().includes(s.name.toLowerCase())
                  );
                  // Recupera l'email primaria dal cantiere trovato
                  const siteEmail = site?.emails?.find(e => e.is_primary)?.address || site?.emails?.[0]?.address || '';
                  setFormData(prev => ({
                    ...prev,
                    ticket: ticket.ticket_number,
                    ticket_id: ticket.ticket_id,
                    site_id: site?.id || prev.site_id,
                    client_email: siteEmail || prev.client_email,
                    notes: ticket.description ? `[${ticket.ticket_number}] ${ticket.description}` : prev.notes,
                    activity_type: ticket.subject?.toLowerCase().includes('pulizia') ? 'Pulizia straordinaria' : 
                                  ticket.subject?.toLowerCase().includes('sanific') ? 'Sanificazione' : prev.activity_type
                  }));
                  toast.success(`Ticket ${ticket.ticket_number} selezionato`);
                }
              }}
            >
              <SelectTrigger className="bg-white">
                <SelectValue placeholder="Seleziona un ticket..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- Nessun ticket --</SelectItem>
                {pendingTickets.map(ticket => (
                  <SelectItem key={ticket.ticket_id} value={ticket.ticket_id}>
                    <div className="flex flex-col">
                      <span className="font-medium">{ticket.ticket_number} - {ticket.company_name}</span>
                      <span className="text-xs text-slate-500">{ticket.cantiere_name} • {ticket.subject}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Dettagli ticket selezionato */}
            {selectedTicket && (
              <div className="bg-white rounded-md p-3 text-sm space-y-1 border border-blue-100">
                <div className="flex justify-between">
                  <span className="text-slate-500">Cliente:</span>
                  <span className="font-medium">{selectedTicket.company_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Cantiere:</span>
                  <span className="font-medium">{selectedTicket.cantiere_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Priorità:</span>
                  <span className={`font-medium ${selectedTicket.priority === 'alta' ? 'text-red-600' : selectedTicket.priority === 'media' ? 'text-amber-600' : 'text-green-600'}`}>
                    {selectedTicket.priority || 'normale'}
                  </span>
                </div>
                {selectedTicket.ore_previste && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ore previste:</span>
                    <span className="font-medium">{selectedTicket.ore_previste}h</span>
                  </div>
                )}
                {selectedTicket.description && (
                  <div className="pt-2 border-t">
                    <span className="text-slate-500 block mb-1">Descrizione:</span>
                    <p className="text-slate-700">{selectedTicket.description}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 flex items-center gap-2 text-slate-500">
            <FileText className="w-4 h-4" />
            <span className="text-sm">Nessun ticket clienti in attesa</span>
          </div>
        )
      )}

      {/* Photo Upload - sempre visibile */}
      <div className="bg-blue-50 rounded-lg p-4">
        <PhotoUpload
          reportId={editingReport?.id}
          photos={editingReport?.photos || []}
          onPhotosChange={(newPhotos) => {
            // Update local state for existing reports
          }}
          pendingPhotos={pendingPhotos}
          onPendingPhotosChange={setPendingPhotos}
          disabled={false}
        />
      </div>

      {/* Summary */}
      <div className="bg-gradient-to-r from-[#1B6B93] to-[#4BA3C7] text-white rounded-lg p-4">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold">{totals.numWorkdays}</p>
            <p className="text-xs opacity-80">Giornate</p>
          </div>
          <div>
            <p className="text-2xl font-bold">{totals.numEmployees}</p>
            <p className="text-xs opacity-80">Dipendenti</p>
          </div>
          <div>
            <p className="text-2xl font-bold">h {totals.totalHours.toFixed(2)}</p>
            <p className="text-xs opacity-80">Ore Totali</p>
          </div>
        </div>
      </div>

      {/* Workdays */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-lg font-semibold text-[#1B6B93]">Giornate Lavorative</Label>
        </div>

        {formData.workdays.map((day, dayIndex) => (
          <Card key={dayIndex} className="border-l-4 border-l-[#1B6B93]">
            <CardContent className="p-3">
              {/* Day Header */}
              <div className="flex items-center justify-between cursor-pointer" onClick={() => toggleDayExpanded(dayIndex)}>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-[#1B6B93]" />
                  <span className="font-semibold">Giornata {dayIndex + 1}</span>
                  <span className="text-sm text-slate-500">{day.date}</span>
                  <span className="text-sm text-slate-500">({day.employees.length} dip.)</span>
                  {day.activity_type && day.activity_type !== formData.activity_type && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">{day.activity_type}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-[#1B6B93]">
                    h {day.employees.reduce((sum, e) => sum + (e.hours || 0), 0).toFixed(2)}
                  </span>
                  {expandedDays.includes(dayIndex) ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </div>

              {/* Day Details */}
              {expandedDays.includes(dayIndex) && (
                <div className="mt-4 space-y-4">
                  {/* Date and Time */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Data</Label>
                      <Input
                        type="date"
                        value={day.date}
                        onChange={(e) => updateWorkday(dayIndex, 'date', e.target.value)}
                        className="bg-white h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Inizio</Label>
                      <Input
                        type="time"
                        value={day.start_time}
                        onChange={(e) => updateWorkday(dayIndex, 'start_time', e.target.value)}
                        className="bg-white h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Fine</Label>
                      <Input
                        type="time"
                        value={day.end_time}
                        onChange={(e) => updateWorkday(dayIndex, 'end_time', e.target.value)}
                        className="bg-white h-9"
                      />
                    </div>
                  </div>

                  {/* Activity type override per giornata */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Attività giornata (lascia vuoto per usare quella principale)</Label>
                    <Select
                      value={day.activity_type || '_inherit'}
                      onValueChange={(value) => updateWorkday(dayIndex, 'activity_type', value === '_inherit' ? '' : value)}
                    >
                      <SelectTrigger className="bg-white h-9">
                        <SelectValue placeholder="Usa attività principale" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_inherit">-- Usa attività principale --</SelectItem>
                        {ACTIVITY_TYPES.map(a => (<SelectItem key={a} value={a}>{a}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Employees for this day */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-[#1B6B93]" />
                      <Label className="text-sm">Dipendenti</Label>
                      <Select onValueChange={(empId) => {
                        const emp = employees.find(e => e.id === empId);
                        if (emp) addEmployeeToDay(dayIndex, emp);
                      }}>
                        <SelectTrigger className="w-48 h-8 text-xs">
                          <SelectValue placeholder="+ Aggiungi" />
                        </SelectTrigger>
                        <SelectContent>
                          {employees.map(emp => (
                            <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {day.employees.length > 0 ? (
                      <div className="space-y-2">
                        {day.employees.map((emp, empIndex) => (
                          <div key={empIndex} className="bg-slate-50 p-2 rounded">
                            {/* Mobile: layout verticale */}
                            <div className="flex flex-col gap-2 md:hidden">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-medium">{emp.employee_name}</span>
                                <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeEmployeeFromDay(dayIndex, empIndex)}>
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                              <div className="flex items-center gap-2">
                                <Input
                                  type="time"
                                  value={emp.start_time}
                                  onChange={(e) => updateEmployeeInDay(dayIndex, empIndex, 'start_time', e.target.value)}
                                  className="flex-1 h-9 text-sm bg-white"
                                />
                                <span className="text-xs">-</span>
                                <Input
                                  type="time"
                                  value={emp.end_time}
                                  onChange={(e) => updateEmployeeInDay(dayIndex, empIndex, 'end_time', e.target.value)}
                                  className="flex-1 h-9 text-sm bg-white"
                                />
                                <span className="text-sm font-medium text-[#1B6B93] whitespace-nowrap">h {emp.hours?.toFixed(2)}</span>
                              </div>
                            </div>
                            {/* Desktop: layout orizzontale */}
                            <div className="hidden md:flex items-center gap-2">
                              <span className="flex-1 text-sm font-medium">{emp.employee_name}</span>
                              <Input
                                type="time"
                                value={emp.start_time}
                                onChange={(e) => updateEmployeeInDay(dayIndex, empIndex, 'start_time', e.target.value)}
                                className="w-24 h-7 text-xs bg-white"
                              />
                              <span className="text-xs">-</span>
                              <Input
                                type="time"
                                value={emp.end_time}
                                onChange={(e) => updateEmployeeInDay(dayIndex, empIndex, 'end_time', e.target.value)}
                                className="w-24 h-7 text-xs bg-white"
                              />
                              <span className="text-xs font-medium w-16">h {emp.hours?.toFixed(2)}</span>
                              <Button type="button" size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => removeEmployeeFromDay(dayIndex, empIndex)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 italic">Nessun dipendente aggiunto</p>
                    )}
                  </div>

                  {/* Equipment for this day */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-4 h-4 text-[#4BA3C7]" />
                      <Label className="text-sm">Attrezzature</Label>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => addEquipmentToDay(dayIndex)}>
                        <Plus className="w-3 h-3 mr-1" /> Aggiungi
                      </Button>
                    </div>

                    {day.equipment.map((eq, eqIndex) => (
                      <div key={eqIndex} className="bg-slate-50 p-3 rounded space-y-2">
                        {/* Attrezzatura select + delete */}
                        <div className="flex items-center gap-2">
                          <Select 
                            value={eq.equipment_name} 
                            onValueChange={(v) => updateEquipmentInDay(dayIndex, eqIndex, 'equipment_name', v)}
                          >
                            <SelectTrigger className="flex-1 h-9 text-sm bg-white">
                              <SelectValue placeholder="Seleziona attrezzatura" />
                            </SelectTrigger>
                            <SelectContent>
                              {equipmentSuggestions.map(eqName => (
                                <SelectItem key={eqName} value={eqName}>{eqName}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button type="button" size="sm" variant="ghost" className="h-9 w-9 p-0 text-red-500" onClick={() => removeEquipmentFromDay(dayIndex, eqIndex)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                        {/* Orari dalle/alle */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs text-slate-500 w-10">Dalle:</span>
                          <input
                            type="time"
                            value={eq.start_time || day.start_time || ''}
                            onChange={(e) => updateEquipmentInDay(dayIndex, eqIndex, 'start_time', e.target.value)}
                            className="w-24 h-8 text-sm px-2 border border-gray-300 rounded bg-white"
                          />
                          <span className="text-xs text-slate-500 w-10">Alle:</span>
                          <input
                            type="time"
                            value={eq.end_time || day.end_time || ''}
                            onChange={(e) => updateEquipmentInDay(dayIndex, eqIndex, 'end_time', e.target.value)}
                            className="w-24 h-8 text-sm px-2 border border-gray-300 rounded bg-white"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Note giornata */}
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Note giornata (dettaglio lavori svolti)</Label>
                    <textarea
                      value={day.notes || ''}
                      onChange={(e) => updateWorkday(dayIndex, 'notes', e.target.value)}
                      placeholder="Descrivi i lavori svolti in questa giornata..."
                      className="w-full p-2 border rounded-lg text-sm bg-white resize-none min-h-[60px]"
                      rows={2}
                      data-testid={`workday-notes-${dayIndex}`}
                    />
                  </div>

                  {/* Remove Day Button */}
                  {formData.workdays.length > 1 && (
                    <div className="pt-2 border-t">
                      <Button type="button" size="sm" variant="destructive" onClick={() => removeWorkday(dayIndex)}>
                        <Trash2 className="w-4 h-4 mr-1" /> Rimuovi Giornata
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {/* Pulsante Aggiungi Giornata - posizionato dopo l'ultima giornata */}
        <Button type="button" onClick={addWorkday} className="w-full bg-green-600 hover:bg-green-700">
          <Plus className="w-4 h-4 mr-2" /> Aggiungi Giornata
        </Button>
      </div>

      {/* Toggle PDF senza ore per cliente */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-center space-x-3">
          <input
            type="checkbox"
            id="clientReportNoHoursMulti"
            checked={formData.client_report_no_hours}
            onChange={(e) => setFormData({ ...formData, client_report_no_hours: e.target.checked })}
            className="w-5 h-5 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
          />
          <label htmlFor="clientReportNoHoursMulti" className="text-sm font-medium text-amber-800 cursor-pointer flex items-center gap-2">
            <FileText className="w-4 h-4" /> Genera PDF per Cliente (senza indicazione ore)
          </label>
        </div>
        <p className="text-xs text-amber-600 mt-2 ml-8">
          Il PDF mostrerà solo l'attività svolta, senza dettagli su ore e orari di lavoro
        </p>
      </div>

      {/* Signatures Section */}
      <div className="border-t pt-4">
        <h3 className="text-lg font-semibold text-[#1B6B93] mb-4">Firme</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Employee Signature */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Nome e Cognome Dipendente Firmatario {!isAdminOrGestore() && '*'}</Label>
              <Input 
                value={formData.employee_signer_name} 
                onChange={(e) => setFormData({ ...formData, employee_signer_name: e.target.value })} 
                placeholder="Mario Rossi" 
                required={!isAdminOrGestore()} 
                className="bg-white" 
              />
            </div>
            <SignaturePad 
              label={`Firma Dipendente ${isAdminOrGestore() ? '(opzionale)' : ''}`}
              required={!isAdminOrGestore()}
              savedSignature={formData.employee_signature} 
              onSave={(sig) => setFormData({ ...formData, employee_signature: sig })} 
              onClear={() => setFormData({ ...formData, employee_signature: '' })} 
            />
          </div>
          
          {/* Client Signature */}
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Nome e Cognome Cliente Firmatario {!isAdminOrGestore() && '*'}</Label>
              <Input 
                value={formData.client_signer_name} 
                onChange={(e) => setFormData({ ...formData, client_signer_name: e.target.value })} 
                placeholder="Giuseppe Verdi" 
                required={!isAdminOrGestore()} 
                className="bg-white" 
              />
            </div>
            <SignaturePad 
              label={`Firma Cliente ${isAdminOrGestore() ? '(opzionale)' : ''}`}
              required={!isAdminOrGestore()}
              savedSignature={formData.client_signature} 
              onSave={(sig) => setFormData({ ...formData, client_signature: sig })} 
              onClear={() => setFormData({ ...formData, client_signature: '' })} 
            />
          </div>
        </div>
      </div>

      {/* Approval Checkbox - Only for admin/gestore without client signature */}
      {showApprovalCheckbox && !formData.skip_email_send && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="sendApprovalEmailMultiDay"
              checked={formData.send_approval_email}
              onChange={(e) => setFormData({ ...formData, send_approval_email: e.target.checked })}
              className="w-5 h-5 text-purple-600 rounded border-purple-300 focus:ring-purple-500"
            />
            <label htmlFor="sendApprovalEmailMultiDay" className="text-sm font-medium text-purple-800 cursor-pointer flex items-center gap-2">
              <Mail className="w-4 h-4" /> Invia richiesta di approvazione al cliente
            </label>
          </div>
          <p className="text-xs text-purple-600 mt-2 ml-8">
            Il cliente riceverà un'email con un link per approvare o rifiutare il rapporto online
          </p>
        </div>
      )}

      {/* Skip Email Checkbox - SOLO ADMIN */}
      {user?.role === 'admin' && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="skipEmailSendMultiDay"
              checked={formData.skip_email_send}
              onChange={(e) => {
                setFormData({ 
                  ...formData, 
                  skip_email_send: e.target.checked,
                  send_approval_email: e.target.checked ? false : formData.send_approval_email
                });
              }}
              className="w-5 h-5 text-amber-600 rounded border-amber-300 focus:ring-amber-500"
            />
            <label htmlFor="skipEmailSendMultiDay" className="text-sm font-medium text-amber-800 cursor-pointer">
              📋 Rapporto già consegnato (non inviare email)
            </label>
          </div>
          <p className="text-xs text-amber-600 mt-2 ml-8">
            Usa questa opzione per caricare rapporti cartacei già consegnati al cliente
          </p>
        </div>
      )}

      {/* Submit Buttons - pb-16 to avoid "Made with Emergent" watermark on mobile */}
      <div className="flex flex-wrap justify-end gap-3 pt-4 pb-16 md:pb-4 border-t">
        <Button type="button" variant="outline" onClick={onCancel}>Annulla</Button>
        {/* Salva Bozza - per admin/gestore, utenti con permesso bozze, o dipendenti che modificano rapporto resettato */}
        {(isAdminOrGestore() || user?.can_create_drafts) && (
          <Button 
            type="button" 
            variant="outline" 
            className="border-amber-500 text-amber-600 hover:bg-amber-50"
            onClick={(e) => handleSubmit(e, true)}
            disabled={submitting}
          >
            <FileText className="w-4 h-4 mr-2" />
            Salva Bozza
          </Button>
        )}
        <Button 
          type="submit" 
          className="bg-[#1B6B93] hover:bg-[#155a7a]" 
          disabled={submitting || (!isAdminOrGestore() && (!formData.employee_signature || !formData.client_signature))}
        >
          {submitting ? (
            <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>{editingReport ? 'Aggiornamento...' : 'Creazione...'}</>
          ) : (
            editingReport 
              ? 'Aggiorna Rapporto' 
              : (formData.send_approval_email && !formData.client_signature 
                  ? 'Crea e Invia Approvazione' 
                  : 'Crea Rapporto Multi-Giorno')
          )}
        </Button>
      </div>
    </form>
  );
};

export default MultiDayReportForm;
